import { createContext, ReactNode, useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CompanyDto, UserDto } from "@normax-performance/shared-types";
import { authApi } from "../../lib/api-client/endpoints";
import { ApiError } from "../../lib/api-client/client";

interface AuthContextValue {
  user: UserDto | null;
  company: CompanyDto | null;
  isLoading: boolean;
  login: (companyCode: string, email: string, password: string) => Promise<void>;
  loginError: string | null;
  isLoggingIn: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });

  const loginMutation = useMutation({
    mutationFn: ({ companyCode, email, password }: { companyCode: string; email: string; password: string }) =>
      authApi.login(companyCode, email, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      queryClient.setQueryData(["auth", "me"], null);
      queryClient.clear();
    },
  });

  const value: AuthContextValue = {
    user: meQuery.data?.user ?? null,
    company: meQuery.data?.company ?? null,
    isLoading: meQuery.isLoading,
    login: async (companyCode, email, password) => {
      await loginMutation.mutateAsync({ companyCode, email, password });
    },
    loginError: loginMutation.error instanceof ApiError ? loginMutation.error.message : null,
    isLoggingIn: loginMutation.isPending,
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
